import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import Image from '../AppImage';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../hooks';
import { getTodayAlertsCount } from '../../lib/api';
import {
  getDefaultAuthenticatedPath,
  hasActiveSubscription,
  canAccessWelcomeGuide,
  needsSubscriptionSelection,
} from '../../lib/utils/subscriptionAccess';
import { planHasAtsIntegration, planHasVoiceAI } from '../../lib/utils/planEntitlements';
import { canAccessDebug } from '../../lib/utils/debugAccess';
import { isDemoIncomplete, isDemoStepCompleted, isDemoStepId, isDemoStepUpcoming, isDemoCompleteView, hasSkippedDemoTestCall, DEMO_STEP_IDS } from '../../lib/demo/demoSteps';

const AI_ASSISTANT_BASE_PATH = '/ai-assistant';
const AI_ASSISTANT_DEFAULT_PATH = `${AI_ASSISTANT_BASE_PATH}/overview`;

const Sidebar = ({ isOpen = false, onClose }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, signOut } = useAuth();
  const { profile } = useProfile();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [aiAssistantOpen, setAiAssistantOpen] = useState(false);
  const [todayAlertsCount, setTodayAlertsCount] = useState(0);

  const getInitials = (name) => {
    if (!name) return null;
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const initials = getInitials(profile?.full_name);

  const plan = profile?.subscription_plan?.toLowerCase() || null;
  const hasVoiceAI = planHasVoiceAI(plan);
  const hasAtsIntegration = planHasAtsIntegration(plan);
  const billingOnlyMode = needsSubscriptionSelection(profile);
  const isOnDemoPage = location.pathname === '/demo';
  const isDemoMode = isOnDemoPage && (
    isDemoIncomplete(profile) || canAccessWelcomeGuide(profile)
  );
  const showWelcomeGuide = canAccessWelcomeGuide(profile);
  const demoQueryStep = searchParams.get('step');
  const isDemoCompleteScreen = isDemoMode && isDemoCompleteView(demoQueryStep);
  const demoStepId = isDemoMode && isDemoStepId(demoQueryStep)
    ? demoQueryStep
    : null;
  const isPastDue = profile?.subscription_status === 'past_due';
  const isPaymentRestricted = hasActiveSubscription(profile) && isPastDue;
  const homePath = isDemoMode ? '/demo' : getDefaultAuthenticatedPath(profile);
  const isOnAiAssistantRoute = location.pathname.startsWith(AI_ASSISTANT_BASE_PATH);

  useEffect(() => {
    if (isOnAiAssistantRoute || demoStepId === 'ai-assistant') {
      setAiAssistantOpen(true);
    }
  }, [isOnAiAssistantRoute, demoStepId]);

  useEffect(() => {
    const fetchTodayAlertsCount = async () => {
      if (!user?.id || isDemoMode || billingOnlyMode) {
        setTodayAlertsCount(0);
        return;
      }

      try {
        const count = await getTodayAlertsCount(user.id);
        setTodayAlertsCount(count);
      } catch (error) {
        console.error('Error fetching today alerts count:', error);
      }
    };

    fetchTodayAlertsCount();
  }, [user?.id, location.pathname, billingOnlyMode, isDemoMode]);

  const menuItems = useMemo(() => {
    const aiChildren = [
      { label: t('nav.overview'), path: `${AI_ASSISTANT_BASE_PATH}/overview` },
      { label: t('nav.aiCalls'), path: `${AI_ASSISTANT_BASE_PATH}/calls` },
      { label: t('nav.setup'), path: `${AI_ASSISTANT_BASE_PATH}/setup` },
    ];

    const allItems = [
      { id: 'dashboard', label: t('nav.dashboard'), path: '/dashboard', icon: 'LayoutDashboard', tooltip: t('nav.dashboardTooltip') },
      { id: 'alerts', label: t('nav.alerts'), path: '/alerts', icon: 'Bell', tooltip: t('nav.alertsTooltip') },
      { id: 'candidates', label: t('nav.candidates'), path: '/candidates-list', icon: 'Users', tooltip: t('nav.candidatesTooltip') },
      { id: 'import-candidates', label: t('nav.importCandidates'), path: '/import-candidates', icon: 'Download', tooltip: t('nav.importCandidatesTooltip') },
      { id: 'connect-ats', label: t('nav.connectAts'), path: '/connect-ats', icon: 'Link', tooltip: t('nav.connectAtsTooltip'), atsOnly: true },
      {
        id: 'ai-assistant',
        label: t('nav.aiAssistant'),
        icon: 'Bot',
        tooltip: t('nav.aiAssistantTooltip'),
        voiceAiOnly: true,
        isGroup: true,
        basePath: AI_ASSISTANT_BASE_PATH,
        defaultPath: AI_ASSISTANT_DEFAULT_PATH,
        children: aiChildren,
      },
      { id: 'billing', label: t('nav.billing'), path: '/billing', icon: 'CreditCard', tooltip: t('nav.billingTooltip') },
      { id: 'faq', label: t('nav.faq'), path: '/faq', icon: 'HelpCircle', tooltip: t('nav.faqTooltip') },
    ];

    const items = billingOnlyMode && !isDemoMode
      ? [
        ...(showWelcomeGuide
          ? [{
            id: 'welcome-guide',
            label: t('nav.welcomeGuide'),
            path: hasSkippedDemoTestCall(profile?.demo_steps_completed)
              ? '/demo?step=test-call'
              : '/demo?step=welcome',
            icon: 'Compass',
            tooltip: t('nav.welcomeGuideTooltip'),
          }]
          : []),
        ...allItems.filter((item) => item.id === 'billing' || item.id === 'faq'),
      ]
      : isDemoMode
        ? [
          {
            id: 'welcome',
            label: t('demo.steps.welcome.title'),
            path: '/demo',
            icon: 'Video',
            tooltip: t('demo.steps.welcome.title'),
          },
          ...allItems.filter((item) => DEMO_STEP_IDS.includes(item.id)),
          {
            id: 'test-scrape',
            label: t('demo.steps.test-scrape.title'),
            path: '/demo',
            icon: 'Play',
            tooltip: t('demo.steps.test-scrape.title'),
          },
          {
            id: 'candidate-profile',
            label: t('demo.steps.candidate-profile.title'),
            path: '/demo',
            icon: 'User',
            tooltip: t('demo.steps.candidate-profile.title'),
          },
          {
            id: 'test-call',
            label: t('demo.steps.test-call.title'),
            path: '/demo',
            icon: 'Phone',
            tooltip: t('demo.steps.test-call.title'),
          },
        ]
        : allItems;

    return items.map((item) => {
      if (isDemoMode) {
        return {
          ...item,
          disabled: false,
          tooltip: item.tooltip,
          path: item.path,
          activePath: item.isGroup ? item.basePath : item.path,
        };
      }

      const disabledByPlan = !billingOnlyMode && (
        (item.voiceAiOnly && !hasVoiceAI) || (item.atsOnly && !hasAtsIntegration)
      );
      const disabledByRestriction = !billingOnlyMode && (
        item.isGroup
          ? isPaymentRestricted
          : isPaymentRestricted && !['/dashboard', '/alerts', '/candidates-list', '/billing', '/faq'].includes(item.path)
      );
      const disabled = disabledByPlan || disabledByRestriction;

      let tooltip = item.tooltip;
      if (disabledByRestriction) {
        tooltip = t('nav.upgradePayment');
      } else if (disabledByPlan) {
        tooltip = item.voiceAiOnly ? t('nav.upgradeVoice') : t('nav.upgradePro');
      }

      const path = item.isGroup
        ? (disabled ? '/billing' : item.defaultPath)
        : (disabledByRestriction ? item.path : (disabled ? '/billing' : item.path));

      return {
        ...item,
        disabled,
        tooltip,
        path,
        activePath: item.isGroup ? item.basePath : item.path,
      };
    });
  }, [billingOnlyMode, isDemoMode, showWelcomeGuide, profile?.demo_steps_completed, hasVoiceAI, hasAtsIntegration, isPaymentRestricted, t, i18n.language]);

  const handleNavigation = (path) => {
    navigate(path);
    onClose?.();
  };

  const handleAiAssistantToggle = (item) => {
    if (isDemoMode) return;

    if (item.disabled) {
      handleNavigation('/billing');
      return;
    }

    if (aiAssistantOpen && isOnAiAssistantRoute) {
      setAiAssistantOpen(false);
      return;
    }

    setAiAssistantOpen(true);
    if (!isOnAiAssistantRoute) {
      handleNavigation(item.defaultPath);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/authentication', { replace: true });
      onClose?.();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const isDemoItemCompleted = (stepId) => (
    isDemoMode && isDemoStepCompleted(stepId, profile?.demo_steps_completed)
  );
  const isDemoItemSkipped = (stepId) => (
    isDemoMode && stepId === 'test-call' && hasSkippedDemoTestCall(profile?.demo_steps_completed)
  );
  const demoCurrentStepId = isDemoCompleteScreen ? null : (demoStepId || DEMO_STEP_IDS[0]);

  const renderDemoStatusIcon = (stepId, isActive) => {
    if (isDemoItemSkipped(stepId)) {
      return (
        <Icon
          name="X"
          size={16}
          className={isActive ? 'text-primary-foreground' : 'text-destructive'}
        />
      );
    }
    if (isDemoItemCompleted(stepId)) {
      return (
        <Icon
          name="Check"
          size={16}
          className={isActive ? 'text-primary-foreground' : 'text-primary'}
        />
      );
    }
    return null;
  };

  const isItemActive = (item) => {
    if (isDemoMode) {
      return Boolean(demoStepId) && item.id === demoStepId;
    }
    if (item.isGroup) {
      return isOnAiAssistantRoute;
    }
    return location.pathname === item.activePath;
  };

  return (
    <>
      {isOpen && (
        <div
          className="sidebar-overlay"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <aside
        className={`sidebar ${isOpen ? 'open' : ''}`}
        role="navigation"
        aria-label={t('common.mainNavigation')}
      >
        <Link to={homePath} className="sidebar-header" aria-label="HirePortal">
          <img
            src="/assets/brand/hireportal-logo.svg"
            alt="HirePortal"
            className="h-10 w-auto max-w-full"
          />
        </Link>

        <nav className="sidebar-nav custom-scrollbar" style={{ height: 'calc(100% - 180px)', overflowY: 'auto' }}>
          {menuItems.map((item) => {
            if (item.isGroup) {
              const groupActive = isItemActive(item);
              const groupUpcoming = isDemoMode && !isDemoCompleteScreen && isDemoStepUpcoming(item.id, demoCurrentStepId);
              const itemClassName = `sidebar-nav-item ${groupActive ? 'active' : ''} ${
                item.disabled ? 'opacity-60 cursor-pointer hover:opacity-80' : ''
              } ${isDemoMode ? 'pointer-events-none' : ''} ${
                groupUpcoming ? 'demo-inactive' : ''
              }`;

              return (
                <div key={item.id} className="flex flex-col gap-1">
                  {isDemoMode ? (
                    <div
                      className={itemClassName}
                      title={item.tooltip}
                      aria-current={groupActive ? 'page' : undefined}
                    >
                      <Icon name={item.icon} size={20} />
                      <span className="flex-1 text-left">{item.label}</span>
                      {renderDemoStatusIcon(item.id, groupActive)}
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleAiAssistantToggle(item)}
                      className={itemClassName}
                      title={item.tooltip}
                      aria-expanded={aiAssistantOpen}
                      aria-current={groupActive ? 'page' : undefined}
                    >
                      <Icon name={item.icon} size={20} />
                      <span className="flex-1 text-left">{item.label}</span>
                      <Icon
                        name="ChevronDown"
                        size={16}
                        className={`transition-transform duration-250 ${aiAssistantOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                  )}

                  {aiAssistantOpen && !item.disabled && !isDemoMode && (
                    <div className="sidebar-nav-sub">
                      {item.children.map((child) => (
                        <button
                          key={child.path}
                          type="button"
                          onClick={() => handleNavigation(child.path)}
                          className={`sidebar-nav-sub-item ${location.pathname === child.path ? 'active' : ''}`}
                          aria-current={location.pathname === child.path ? 'page' : undefined}
                        >
                          <span>{child.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            const itemUpcoming = isDemoMode && !isDemoCompleteScreen && isDemoStepUpcoming(item.id, demoCurrentStepId);
            const itemClassName = `sidebar-nav-item ${isItemActive(item) ? 'active' : ''} ${
              item.disabled ? 'opacity-60 cursor-pointer hover:opacity-80' : ''
            } ${isDemoMode ? 'pointer-events-none' : ''} ${
              itemUpcoming ? 'demo-inactive' : ''
            }`;

            if (isDemoMode) {
              return (
                <div
                  key={item.id}
                  className={itemClassName}
                  title={item.tooltip}
                  aria-current={isItemActive(item) ? 'page' : undefined}
                >
                  <Icon name={item.icon} size={20} />
                  <span className="flex-1 text-left">{item.label}</span>
                  {renderDemoStatusIcon(item.id, isItemActive(item))}
                </div>
              );
            }

            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleNavigation(item.path)}
                className={itemClassName}
                title={item.tooltip}
                aria-current={isItemActive(item) ? 'page' : undefined}
                aria-disabled={item.disabled || undefined}
              >
                <Icon name={item.icon} size={20} />
                <span className="flex-1 text-left">{item.label}</span>
                {item.id === 'alerts' && todayAlertsCount > 0 && (
                  <span
                    className={`inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-xs font-semibold leading-none ${
                      isItemActive(item)
                        ? 'bg-primary-foreground text-primary'
                        : 'bg-primary text-primary-foreground'
                    }`}
                    aria-label={`${todayAlertsCount} alerts today`}
                  >
                    {todayAlertsCount > 99 ? '99+' : todayAlertsCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="user-dropdown">
            <button
              type="button"
              onClick={toggleDropdown}
              className="user-dropdown-trigger w-full"
              aria-expanded={dropdownOpen}
              aria-haspopup="true"
            >
              <div className="flex items-center justify-center min-w-10 h-10 rounded-full bg-primary text-primary-foreground font-medium overflow-hidden flex-shrink-0">
                {profile?.avatar_url ? (
                  <Image src={profile.avatar_url} alt="" className="w-full h-full object-cover" />
                ) : initials ? (
                  <span className="text-sm">{initials}</span>
                ) : (
                  <Icon name="User" size={20} />
                )}
              </div>
              <div className="flex-1 text-left">
                <div className="text-sm font-medium text-foreground">
                  {profile?.full_name || user?.email?.split('@')[0] || 'User'}
                </div>
                <div className="text-xs text-muted-foreground overflow-hidden text-ellipsis w-[125px]" title={user?.email || 'user@example.com'}>
                  {user?.email || 'user@example.com'}
                </div>
              </div>
              <Icon
                name="ChevronUp"
                size={16}
                className={`transition-transform duration-250 ${dropdownOpen ? 'rotate-180' : ''}`}
              />
            </button>

            <div className={`user-dropdown-menu ${dropdownOpen ? 'open' : ''}`}>
              <button
                type="button"
                onClick={() => {
                  handleNavigation('/profile');
                  setDropdownOpen(false);
                }}
                className="user-dropdown-item w-full"
              >
                <Icon name="User" size={16} />
                <span>{t('nav.profile')}</span>
              </button>
              {canAccessDebug(user?.email) && (
                <button
                  type="button"
                  onClick={() => {
                    handleNavigation('/debug');
                    setDropdownOpen(false);
                  }}
                  className="user-dropdown-item w-full"
                >
                  <Icon name="Bug" size={16} />
                  <span>{t('nav.debug')}</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  handleLogout();
                  setDropdownOpen(false);
                }}
                className="user-dropdown-item w-full text-error"
              >
                <Icon name="LogOut" size={16} />
                <span>{t('nav.signOut')}</span>
              </button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
